<template>
    <div>
        <div class="text-center mb-6">
            <v-icon size="48" color="primary" class="mb-2">mdi-access-point</v-icon>
            <h3 class="text-h6 font-weight-bold">Activate Your Device</h3>
            <p class="text-body-2 text-grey-darken-1">
                {{ subStep === 1
                    ? 'Enter the serial number, then the last 5 digits of the IMEI from the device label'
                    : 'Choose a subscription plan for this device' }}
            </p>
        </div>

        <v-form v-model="formValid" validate-on="input lazy" @submit.prevent="activate">
            <v-alert v-if="errors.length" class="mb-4" type="error" density="compact" variant="tonal" closable>
                <div v-for="error in errors" :key="error">{{ error }}</div>
                <!-- Activation can fail for reasons the customer cannot see or fix
                     (a device held back by an admin, a provisioning gap). Always
                     offer a way out rather than leaving them re-typing. -->
                <div class="mt-2 text-caption">
                    Check the details and try again. If it still will not activate, email
                    <a href="mailto:support@simpligps.com?subject=Device%20activation%20problem"
                       class="text-decoration-underline">support@simpligps.com</a>
                    or call
                    <a href="tel:+18006118842" class="text-decoration-underline">1-800-611-8842</a>,
                    quoting the serial number.
                </div>
            </v-alert>

            <!-- pt-2: .v-window is overflow:hidden, and an outlined field floats its
                 label above the input's top border, so the first field on each step
                 had its label clipped. The padding moves the content inside the box. -->
            <v-window v-model="subStep" class="pt-2">
            <v-window-item :value="1">
            <v-text-field
                ref="serialInput"
                v-model="serial"
                :rules="[v => !!v || 'Serial number is required', v => (v && v.length >= 5) || 'Must be at least 5 characters']"
                label="Serial Number"
                placeholder="Enter serial number"
                prepend-inner-icon="mdi-barcode"
                inputmode="numeric"
                variant="outlined"
                class="mb-2"
                autofocus
                @update:modelValue="onSerialChanged"
            />

            <v-text-field
                v-model="imeiLast5"
                :rules="[v => !!v || 'Required', v => /^\d{5,15}$/.test(v || '') || 'Enter the last 5 digits']"
                label="Last 5 digits of IMEI"
                placeholder="12345"
                prepend-inner-icon="mdi-barcode"
                inputmode="numeric"
                autocomplete="off"
                maxlength="15"
                variant="outlined"
                class="mb-2"
                hint="The label shows the full IMEI. Enter just the last 5 digits."
                persistent-hint
                @update:modelValue="onImeiChanged"
            />

            <v-btn color="primary" size="large" block class="mt-4" type="button"
                   :disabled="!step1Complete || verifying" :loading="verifying"
                   @click="verifyAndContinue">
                Continue
                <v-icon end>mdi-arrow-right</v-icon>
            </v-btn>
            </v-window-item>

            <v-window-item :value="2">
            <div v-if="loadingPlans" class="d-flex align-center ga-2 py-3">
                <v-progress-circular :size="18" :width="2" indeterminate color="primary"/>
                <span class="text-grey text-caption">Loading plans for this device...</span>
            </div>
            <v-alert v-else-if="plansError" density="compact" type="warning" variant="tonal" class="mb-3">
                <div class="d-flex align-center justify-space-between ga-2">
                    <span>{{ plansError }}</span>
                    <v-btn size="x-small" variant="text" :disabled="verifying"
                           @click="verifyAndContinue">Retry</v-btn>
                </div>
            </v-alert>
            <v-select
                v-else-if="plans.length > 0"
                v-model="selectedPlan"
                :items="plans"
                :item-title="(plan) => plan.name + (formatPlanPrice(plan) ? ' - ' + formatPlanPrice(plan) : '')"
                item-value="id"
                label="Subscription Plan"
                prepend-inner-icon="mdi-card-account-details"
                variant="outlined"
                class="mb-2"
            >
                <template #item="{props: itemProps, item}">
                    <v-list-item v-bind="itemProps" :subtitle="formatPlanPrice(item.raw)"/>
                </template>
            </v-select>
            <div v-else-if="!serial || serial.toString().trim().length < 5" class="text-caption text-grey py-2 mb-2">
                Enter serial number to load available plans
            </div>

            <v-expansion-panels variant="accordion" class="mb-4">
                <v-expansion-panel>
                    <v-expansion-panel-title class="text-body-2 py-2">
                        <v-icon size="18" class="mr-2">mdi-tag-outline</v-icon>
                        Have a discount code?
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                        <v-text-field
                            v-model="discountCode"
                            label="Discount Code"
                            variant="outlined"
                            density="compact"
                            hide-details
                            @update:modelValue="onDiscountCodeChanged"
                        >
                            <template #append-inner>
                                <v-btn v-if="discountCode && selectedPlan && !discountPreview"
                                       size="x-small" variant="text" color="primary" :loading="validatingDiscount"
                                       @click="validateDiscountCode">Apply</v-btn>
                                <v-icon v-if="discountPreview" color="success" size="small">mdi-check-circle</v-icon>
                            </template>
                        </v-text-field>
                    </v-expansion-panel-text>
                </v-expansion-panel>
            </v-expansion-panels>

            <v-alert v-if="discountPreview" density="compact" type="success" variant="tonal" class="mb-4">
                <div class="d-flex justify-space-between align-center">
                    <div>
                        <div class="text-caption">{{ discountPreview.discount_type === 'PERCENT_OFF' ? discountPreview.discount_value + '% off' : discountPreview.discount_type === 'FREE_MONTHS' ? 'Free months' : 'Discount applied' }}</div>
                        <div class="text-body-2 font-weight-medium">
                            <span class="text-decoration-line-through text-grey">${{ discountPreview.original_price }}</span>
                            &rarr;
                            <span class="text-success">${{ discountPreview.discounted_price }}</span>
                            <span class="text-caption text-grey ml-1">(save ${{ discountPreview.savings }})</span>
                        </div>
                    </div>
                    <v-btn icon="mdi-close" size="x-small" variant="text" @click="clearDiscountPreview"/>
                </div>
            </v-alert>

            <v-btn
                type="submit"
                color="primary"
                size="large"
                block
                :disabled="subStep !== 2 || !formValid || !selectedPlan || processing"
                :loading="processing"
            >
                <v-icon start>mdi-power</v-icon>
                Activate Device
            </v-btn>

            <v-btn variant="text" size="small" block class="mt-2" type="button" @click="subStep = 1">
                <v-icon start size="16">mdi-arrow-left</v-icon>
                Back to device details
            </v-btn>
            </v-window-item>
            </v-window>
        </v-form>

        <div class="buy-device-cta mt-6 pa-5 text-center rounded-xl">
            <p class="text-body-2 text-grey-darken-2 mb-3">Don't have a tracking device yet?</p>
            <v-btn
                href="https://simpligps.com/shop-gps-trackers/"
                target="_blank"
                color="deep-purple-accent-4"
                variant="tonal"
                size="large"
                rounded="pill"
                class="px-8"
            >
                <v-icon start>mdi-shopping</v-icon>
                Shop Devices
                <v-icon end size="16">mdi-open-in-new</v-icon>
            </v-btn>
        </div>
    </div>
</template>

<script setup>
import {ref, computed, onMounted, onBeforeUnmount, nextTick} from "vue";
import {send} from "@/composables/apiClient";
import {resolveActivationCoords} from "@/composables/activationLocation";
import {useSnackbarStore} from "@/stores/snackBarStore";

const emit = defineEmits(['activated', 'paymentRequired']);
const snackbar = useSnackbarStore();

const serialInput = ref(null);
const serial = ref('');
const subStep = ref(1);
const verifying = ref(false);
// The serial runs in sequence so it is guessable on its own. The last 5 of the
// IMEI is printed on the device label, so it proves the person has the unit in
// hand. The API is what actually enforces it.
const imeiLast5 = ref('');
const onImeiChanged = (value) => {
    const digits = (value || '').toString().replace(/\D/g, '').slice(0, 15);
    if (digits !== value) imeiLast5.value = digits;
};
// Whatever the user typed or pasted, the API only ever compares the last 5.
const imeiLast5Digits = () => (imeiLast5.value || '').toString().slice(-5);
const step1Complete = computed(() =>
    (serial.value || '').toString().trim().length >= 5 && /^\d{5,15}$/.test(imeiLast5.value || '')
);
const selectedPlan = ref(null);
const discountCode = ref('');
const formValid = ref(false);
const processing = ref(false);
const errors = ref([]);
const plans = ref([]);
const loadingPlans = ref(false);
const plansError = ref(null);
const validatingDiscount = ref(false);
const discountPreview = ref(null);
let debounceTimer = null;

onMounted(() => {
    nextTick(() => serialInput.value?.focus());
});

onBeforeUnmount(() => {
    if (debounceTimer) clearTimeout(debounceTimer);
});

const onSerialChanged = (value) => {
    // Filter to digits only (matches Flutter behavior)
    const digitsOnly = (value || '').toString().replace(/[^0-9]/g, '');
    if (digitsOnly !== value) {
        serial.value = digitsOnly;
        return; // v-model update will re-trigger this handler
    }

    // Editing the serial invalidates anything already looked up.
    plans.value = [];
    selectedPlan.value = null;
    plansError.value = null;
    loadingPlans.value = false;
    errors.value = [];
};

/**
 * Verify the device before showing plans.
 *
 * The plan lookup applies the same possession check as activation, so a wrong
 * serial or a wrong IMEI fails HERE, on the screen that owns those two fields,
 * instead of after the user has picked a plan. Reaching step 2 now means the
 * device is real, unclaimed and proven to be in their hands.
 */
async function verifyAndContinue() {
    if (!step1Complete.value || verifying.value) return;
    verifying.value = true;
    errors.value = [];
    plansError.value = null;
    try {
        const response = await send('get', 'subscription-plans/list', {
            slno: serial.value,
            imei_last5: imeiLast5Digits(),
        });
        const data = response.data?.data;
        plans.value = Array.isArray(data) ? data : (data?.data || []);
        if (plans.value.length === 0) {
            // Verified, but nothing to sell against it. Keep them here rather
            // than moving to an empty plan step.
            errors.value.push('No subscription plans are available for this device. Please contact support.');
            return;
        }
        if (plans.value.length === 1) selectedPlan.value = plans.value[0].id;
        subStep.value = 2;
    } catch (error) {
        errors.value.push(
            error.response?.data?.message
            || 'We could not verify this device. Please check the serial number and the last 5 digits of the IMEI.'
        );
    } finally {
        verifying.value = false;
    }
}

const fetchPlans = (slno) => {
    loadingPlans.value = true;
    plansError.value = null;
    send('get', 'subscription-plans/list', {slno})
        .then((response) => {
            const data = response.data?.data;
            plans.value = Array.isArray(data) ? data : (data?.data || []);
            if (plans.value.length === 0) {
                plansError.value = 'No plans available for this device serial';
            } else if (plans.value.length === 1) {
                selectedPlan.value = plans.value[0].id;
            }
        })
        .catch((error) => {
            plansError.value = error.response?.data?.message || 'Failed to load subscription plans';
        })
        .finally(() => {
            loadingPlans.value = false;
        });
};

const onDiscountCodeChanged = () => {
    discountPreview.value = null;
}

const clearDiscountPreview = () => {
    discountPreview.value = null;
    discountCode.value = '';
}

const validateDiscountCode = () => {
    if (!discountCode.value || !selectedPlan.value || !serial.value) return;
    validatingDiscount.value = true;
    send('post', 'devices/validate-discount', {
        slno: serial.value,
        plan: selectedPlan.value,
        discount_code: discountCode.value,
    }).then((response) => {
        if (response.data.success) {
            discountPreview.value = response.data.data;
        }
    }).catch((error) => {
        const msg = error.response?.data?.message || 'Invalid discount code';
        snackbar.showSnackbar(msg, 'error');
    }).finally(() => {
        validatingDiscount.value = false;
    });
}

const formatPlanPrice = (plan) => {
    const price = plan.price;
    if (!price || price === '0' || price === '0.00') return 'Free';
    const intervals = {0: '/month', 1: '/quarter', 2: '/year'};
    return `$${price}${intervals[plan.interval] || ''}`;
};

const activate = async () => {
    errors.value = [];
    processing.value = true;
    const data = {slno: serial.value, plan: selectedPlan.value, imei_last5: imeiLast5Digits()};
    if (discountCode.value) data.discount_code = discountCode.value;

    // Resolve the device's first map position in the BROWSER (company address ->
    // user address -> default). The API never geocodes.
    const coords = await resolveActivationCoords();
    data.latitude = coords.lat;
    data.longitude = coords.lng;

    send('post', 'devices/activate-device', data)
        .then((response) => {
            if (response.data.success) {
                const device = response.data.data.device;
                snackbar.showSnackbar(response.data.message);
                emit('activated', device);
            } else {
                errors.value.push(response.data.message || 'Activation failed. Please try again.');
            }
        })
        .catch((errorResponse) => {
            if (errorResponse.response?.status === 303) {
                emit('paymentRequired');
                return;
            }
            if (errorResponse.response?.data?.errors) {
                const validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (errorResponse.response?.data?.message) {
                errors.value.push(errorResponse.response.data.message);
            } else {
                errors.value.push('Unable to activate device. Please check your connection and try again.');
            }
            snackbar.showSnackbar('Activation failed', 'error');
            // The rejection names the serial and the IMEI, both of which live on
            // step 1, so send the user back to the fields they need to correct.
            subStep.value = 1;
        })
        .finally(() => {
            processing.value = false;
        });
};
</script>

<style scoped>
.buy-device-cta {
    background: linear-gradient(135deg, #f3f0ff 0%, #ede7ff 100%);
    border: 1px dashed #b39ddb;
}
</style>
